import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatChipEditedEvent, MatChipInputEvent } from '@angular/material/chips';
import { ReplaySubject } from 'rxjs';

import { BadgingService } from 'app/modules/social/services/badging/badging.service';
import { CustomValidators } from 'app/utils/custom.validators';
import { Loggable } from 'app/utils/logging/loggable';
import BadgeQuestion from 'entity/BadgeQuestion';
import StandardQuestion from 'entity/StandardQuestion';
import BadgeQuestionType from 'enums/BadgeQuestionType';
import QuestionInputType from 'enums/QuestionInputType';
import QuestionVisibility from 'enums/QuestionVisibility';

const MAX_FIELD_LENGTH = 250;
const MAX_OPTION_LENGTH = 20;

@Component({
  selector: 'app-custom',
  templateUrl: './custom.component.html',
  styleUrl: './custom.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomComponent extends Loggable implements OnInit, OnDestroy {
  @Input() public roomQuestions: BadgeQuestion[] = [];
  @Output() public customQuestion = new EventEmitter<BadgeQuestion[]>();
  private destroyed$ = new ReplaySubject<boolean>(1);
  public badgingForm: FormGroup;
  private editingQuestion?: number;
  protected questionType = [
    { text: 'Short answer', value: QuestionInputType.TEXT },
    { text: 'Paragraph', value: QuestionInputType.PARAGRAPH },
    { text: 'Multi select', value: QuestionInputType.MULTI_SELECT },
    { text: 'Single select', value: QuestionInputType.SINGLE_SELECT },
    { text: 'Numeric', value: QuestionInputType.NUMBER },
  ];
  protected singleMultiSelectOptions: string[] = [];
  protected _customQuestions: BadgeQuestion[] = [];
  protected isCreating = signal(false);
  protected isEditing = signal(false);
  protected loading = signal(true);
  protected maxFieldLength = MAX_FIELD_LENGTH;
  protected maxOptionLength = MAX_OPTION_LENGTH;

  public constructor(
    private formBuilder: FormBuilder,
    private badgingService: BadgingService,
  ) {
    super();

    this.badgingForm = this.formBuilder.group({
      title: [
        '',
        [
          Validators.required,
          Validators.pattern(CustomValidators.notEmojisPattern),
          CustomValidators.maxByteLength(this.maxFieldLength),
        ],
      ],
      description: [
        '',
        [
          Validators.required,
          Validators.pattern(CustomValidators.notEmojisPattern),
          CustomValidators.maxByteLength(this.maxFieldLength),
        ],
      ],
      inputType: ['', Validators.required],
      required: [false, Validators.required],
      featured: [false, Validators.required],
      option: ['', CustomValidators.maxByteLength(this.maxFieldLength)],
    });
  }

  public ngOnInit(): void {
    this.getStandardQuestions().catch((err) => this.error(err));
    this.mountQuestions()
      .catch((err) => this.error(err))
      .finally(() => this.loading.set(false));
  }

  protected createCustomQuestion() {
    if (this.badgingForm.valid) {
      let options;
      if (this.isInputTypeSelect) {
        options = this.singleMultiSelectOptions;
      }

      const { description, featured, inputType, required, title } = this.badgingForm.value;

      const value = {
        description,
        featured,
        inputType,
        required,
        title,
        type: BadgeQuestionType.CUSTOM,
        visibility: QuestionVisibility.ALL,
        options,
      };

      this._customQuestions.push(value);
      this.resetForm();
    }
  }

  public async getStandardQuestions() {
    try {
      const questions = await this.badgingService.getStandardBadgeQuestions();
      const titlesArray = questions.flatMap((question: StandardQuestion) => question.title ?? '');
      this.badgingForm.get('title')?.addValidators(CustomValidators.blacklist(titlesArray));
    } catch (err) {
      this.error('Error while getting standard questions', err);
    }
  }

  protected startEditing(index: number) {
    this.editingQuestion = index;
    const { description, featured, inputType, options, required, title } = this._customQuestions[index];

    if (options) {
      this.singleMultiSelectOptions = options;
    }

    const formItem = {
      description,
      featured,
      inputType,
      title,
      required,
      option: '',
    };

    this.badgingForm.setValue(formItem);

    this.isEditing.set(true);
  }

  protected editCurrentItem() {
    if (typeof this.editingQuestion !== 'number') {
      return;
    }

    let options;
    if (this.isInputTypeSelect) {
      options = this.singleMultiSelectOptions;
    }

    const { description, featured, inputType, required, title } = this.badgingForm.value;

    const newValue = {
      description,
      featured,
      inputType,
      required,
      title,
      type: BadgeQuestionType.CUSTOM,
      visibility: QuestionVisibility.ALL,
      options,
    };

    this._customQuestions.splice(this.editingQuestion, 1, newValue);

    this.editingQuestion = undefined;
    this.resetForm();
  }

  protected removeItem(index: number) {
    this._customQuestions.splice(index, 1);
    this.customQuestion.emit(this._customQuestions);
  }

  protected submitForm() {
    if (this.badgingForm.valid) {
      if (this.isCreating()) {
        this.createCustomQuestion();
      }
      if (this.isEditing()) {
        this.editCurrentItem();
      }
      this.customQuestion.emit(this._customQuestions);
    }
  }

  protected resetForm() {
    this.isCreating.set(false);
    this.isEditing.set(false);
    this.singleMultiSelectOptions = [];

    this.badgingForm.reset();
  }

  private async mountQuestions() {
    const questions = await this.badgingService.getStandardBadgeQuestions();
    const questionsTitle = questions.map((question) => question.title || '');

    this.roomQuestions.forEach((question) => {
      if (!questionsTitle.includes(question.title || '')) {
        this._customQuestions.push(question);
      }
    });

    this.customQuestion.emit(this._customQuestions);
  }

  protected addOption(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    if (value.length > this.maxOptionLength) {
      return;
    }

    if (value) {
      this.singleMultiSelectOptions.push(value);
    }
    event.chipInput!.clear();
  }

  protected editOption(option: string, event: MatChipEditedEvent) {
    const value = event.value.trim();

    if (!value) {
      this.removeOption(option);
      return;
    }

    const index = this.singleMultiSelectOptions.indexOf(option);
    if (index >= 0) {
      this.singleMultiSelectOptions[index] = value;
    }
  }

  protected removeOption(option: string) {
    const index = this.singleMultiSelectOptions.indexOf(option);

    if (index >= 0) {
      this.singleMultiSelectOptions.splice(index, 1);
    }
  }

  public get isChangingInfo() {
    return this.isCreating() || this.isEditing();
  }

  public get buttonText() {
    if (this.isEditing()) {
      return 'Edit';
    }
    if (this.isCreating()) {
      return 'Create';
    }
  }

  public get isInputTypeSelect() {
    const inputType = this.badgingForm.get('inputType')?.value;
    if (!inputType) {
      return false;
    }

    if (inputType === QuestionInputType.SINGLE_SELECT || inputType === QuestionInputType.MULTI_SELECT) {
      return true;
    }

    return false;
  }

  protected getErrorMessage(formControlName: string) {
    const field = this.badgingForm.get(formControlName);

    if (field?.errors?.required) {
      return 'This field is required';
    }

    if (field?.errors?.pattern) {
      return `You can't add emojis to this field`;
    }

    if (field?.errors?.maxByteLength) {
      const title = formControlName === 'option' ? 'Option' : 'This field';
      return `${title} exceeded the character limit`;
    }

    if (field?.errors?.blacklist) {
      return `You can't create a question with this title`;
    }

    return '';
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
